import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { authService } from '../lib/auth';
import { toast } from 'sonner';
import { 
  FileText, Plus, Trash2, Loader2, Package, ArrowDownCircle, 
  ArrowUpCircle, Calculator, Scale, BookOpen, X, AlertTriangle, RefreshCw
} from 'lucide-react';

const PaperStock = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('stock');
  const [paperSpecs, setPaperSpecs] = useState([]);
  const [paperStock, setPaperStock] = useState([]);
  const [inwardRecords, setInwardRecords] = useState([]);
  const [consumptionRecords, setConsumptionRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [products, setProducts] = useState([]);
  
  // Modals
  const [showSpecModal, setShowSpecModal] = useState(false);
  const [showInwardModal, setShowInwardModal] = useState(false);
  const [showConsumptionModal, setShowConsumptionModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Form states
  const [specForm, setSpecForm] = useState({ name: '', width: '', length: '', gsm: '' });
  const [inwardForm, setInwardForm] = useState({ paper_spec_id: '', total_weight_kg: '', supplier_name: '', invoice_number: '', remarks: '' });
  const [consumptionForm, setConsumptionForm] = useState({ paper_spec_id: '', product_id: '', number_of_books: '', pages_per_book: '', remarks: '' });
  
  const user = authService.getCurrentUser();

  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error('Only admin can access Paper Stock');
      return;
    }
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [specsRes, stockRes, inwardRes, consumptionRes, summaryRes, productsRes] = await Promise.all([
        api.getPaperSpecs(),
        api.getPaperStock(),
        api.getPaperInward(),
        api.getPaperConsumption(),
        api.getPaperSummary(),
        api.getStockProducts()  // Use stock products (books) instead of sales products
      ]);
      setPaperSpecs(specsRes.data);
      setPaperStock(stockRes.data);
      setInwardRecords(inwardRes.data);
      setConsumptionRecords(consumptionRes.data);
      setSummary(summaryRes.data);
      setProducts(productsRes.data || []);
    } catch (error) {
      console.error('Error fetching paper stock data:', error);
      toast.error('Failed to load paper stock data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate rim weight preview
  const calculateRimWeight = () => {
    const { width, length, gsm } = specForm;
    if (width && length && gsm) {
      return ((parseFloat(width) * parseFloat(length) * parseFloat(gsm)) / 20000).toFixed(3);
    }
    return null;
  };

  // Calculate rims from weight
  const calculateRimsFromWeight = () => {
    const { paper_spec_id, total_weight_kg } = inwardForm;
    const spec = paperSpecs.find(s => s.id === paper_spec_id);
    if (spec && total_weight_kg) {
      return (parseFloat(total_weight_kg) / spec.rim_weight_kg).toFixed(2);
    }
    return null;
  };

  // Handle product selection - auto-fill pages
  const handleProductChange = (productId) => {
    const product = products.find(p => p.id === productId);
    setConsumptionForm(prev => ({
      ...prev,
      product_id: productId,
      pages_per_book: product?.pages || ''
    }));
  };

  // Calculate consumption preview with 4% wastage
  const calculateConsumption = () => {
    const { paper_spec_id, number_of_books, pages_per_book } = consumptionForm;
    const spec = paperSpecs.find(s => s.id === paper_spec_id);
    if (spec && number_of_books && pages_per_book) {
      const totalPages = parseInt(number_of_books) * parseInt(pages_per_book);
      const baseRims = totalPages / 8000;
      const wastageRims = baseRims * 0.04;  // 4% wastage
      const rimsConsumed = baseRims + wastageRims;
      const weightConsumed = rimsConsumed * spec.rim_weight_kg;
      return {
        totalPages,
        baseRims: baseRims.toFixed(2),
        wastageRims: wastageRims.toFixed(2),
        rimsConsumed: rimsConsumed.toFixed(2),
        weightConsumed: weightConsumed.toFixed(2)
      };
    }
    return null;
  };

  // Handlers
  const handleCreateSpec = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.createPaperSpec({
        name: specForm.name,
        width: parseFloat(specForm.width),
        length: parseFloat(specForm.length),
        gsm: parseFloat(specForm.gsm)
      });
      toast.success('Paper specification created');
      setShowSpecModal(false);
      setSpecForm({ name: '', width: '', length: '', gsm: '' });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create specification');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSpec = async (specId) => {
    if (!window.confirm('Delete this paper specification?')) return;
    try {
      await api.deletePaperSpec(specId);
      toast.success('Specification deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete specification');
    }
  };

  const handleCreateInward = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.createPaperInward({
        paper_spec_id: inwardForm.paper_spec_id,
        total_weight_kg: parseFloat(inwardForm.total_weight_kg),
        supplier_name: inwardForm.supplier_name || null,
        invoice_number: inwardForm.invoice_number || null,
        remarks: inwardForm.remarks || null
      });
      toast.success('Paper inward recorded');
      setShowInwardModal(false);
      setInwardForm({ paper_spec_id: '', total_weight_kg: '', supplier_name: '', invoice_number: '', remarks: '' });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record inward');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateConsumption = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.createPaperConsumption({
        paper_spec_id: consumptionForm.paper_spec_id,
        product_id: consumptionForm.product_id,
        number_of_books: parseInt(consumptionForm.number_of_books),
        pages_per_book: consumptionForm.pages_per_book ? parseInt(consumptionForm.pages_per_book) : null,
        remarks: consumptionForm.remarks || null
      });
      toast.success('Paper consumption recorded');
      setShowConsumptionModal(false);
      setConsumptionForm({ paper_spec_id: '', product_id: '', number_of_books: '', pages_per_book: '', remarks: '' });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record consumption');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMigrateWastage = async () => {
    if (!window.confirm('This will recalculate all existing consumption records to add 4% wastage. Stock levels will be adjusted accordingly. Continue?')) {
      return;
    }
    setSubmitting(true);
    try {
      const response = await api.migratePaperWastage();
      if (response.data.migrated_count === 0) {
        toast.info('No records need migration - all records already have wastage calculated');
      } else {
        toast.success(`Migration complete! ${response.data.migrated_count} records updated. Additional ${response.data.total_additional_rims} rims deducted from stock.`);
        fetchAllData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Migration failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertTriangle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900">Access Denied</h2>
          <p className="text-slate-500">Only admin can access Paper Stock management.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="h-7 w-7 text-cyan-600" />
            Paper Stock Management
          </h1>
          <p className="text-slate-500">Track paper reels, rims, and consumption for printing</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowSpecModal(true)}
            className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors flex items-center gap-2"
          >
            <Plus size={18} />
            Add Paper Type
          </button>
          <button
            onClick={() => setShowInwardModal(true)}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-2"
          >
            <ArrowDownCircle size={18} />
            Paper Inward
          </button>
          <button
            onClick={() => setShowConsumptionModal(true)}
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center gap-2"
          >
            <ArrowUpCircle size={18} />
            Record Consumption
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-xl p-4 text-white">
            <div className="flex items-center gap-3">
              <Package className="h-10 w-10 opacity-80" />
              <div>
                <p className="text-2xl font-bold">{summary.paper_types_count}</p>
                <p className="text-cyan-100 text-sm">Paper Types</p>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-4 text-white">
            <div className="flex items-center gap-3">
              <Scale className="h-10 w-10 opacity-80" />
              <div>
                <p className="text-2xl font-bold">{summary.total_current_weight_kg.toLocaleString()} kg</p>
                <p className="text-emerald-100 text-sm">Current Stock Weight</p>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
            <div className="flex items-center gap-3">
              <FileText className="h-10 w-10 opacity-80" />
              <div>
                <p className="text-2xl font-bold">{summary.total_current_rims.toLocaleString()}</p>
                <p className="text-blue-100 text-sm">Current Rims</p>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-4 text-white">
            <div className="flex items-center gap-3">
              <BookOpen className="h-10 w-10 opacity-80" />
              <div>
                <p className="text-2xl font-bold">{summary.total_consumed_rims.toLocaleString()}</p>
                <p className="text-orange-100 text-sm">Total Consumed Rims</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <div className="flex gap-4">
          {[
            { id: 'stock', label: 'Current Stock', icon: Package },
            { id: 'specs', label: 'Paper Types', icon: FileText },
            { id: 'inward', label: 'Inward History', icon: ArrowDownCircle },
            { id: 'consumption', label: 'Consumption History', icon: ArrowUpCircle }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-cyan-600 text-cyan-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <tab.icon size={18} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {activeTab === 'stock' && (
          <div className="p-6">
            <h2 className="text-lg font-semibold mb-4">Current Paper Stock</h2>
            {paperStock.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Package size={48} className="mx-auto mb-4 text-slate-300" />
                <p>No paper stock recorded yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Paper Type</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600">Rim Weight (kg)</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600">Current Weight (kg)</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600">Current Rims</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600">Total Consumed</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {paperStock.map(stock => (
                      <tr key={stock.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium text-slate-900">{stock.paper_spec_name}</td>
                        <td className="px-4 py-3 text-right text-slate-600">{stock.rim_weight_kg?.toFixed(3)}</td>
                        <td className="px-4 py-3 text-right font-semibold text-emerald-600">{stock.current_weight_kg?.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right font-semibold text-blue-600">{stock.current_rims?.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right text-orange-600">{stock.total_consumed_rims?.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'specs' && (
          <div className="p-6">
            <h2 className="text-lg font-semibold mb-4">Paper Specifications</h2>
            <p className="text-sm text-slate-500 mb-4">Formula: Rim Weight (kg) = Width × Length × GSM / 20,000</p>
            {paperSpecs.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <FileText size={48} className="mx-auto mb-4 text-slate-300" />
                <p>No paper types defined yet</p>
                <button
                  onClick={() => setShowSpecModal(true)}
                  className="mt-4 text-cyan-600 hover:text-cyan-700"
                >
                  Add your first paper type
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Name</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600">Width (cm)</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600">Length (cm)</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600">GSM</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600">Rim Weight (kg)</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {paperSpecs.map(spec => (
                      <tr key={spec.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium text-slate-900">{spec.name}</td>
                        <td className="px-4 py-3 text-right text-slate-600">{spec.width}</td>
                        <td className="px-4 py-3 text-right text-slate-600">{spec.length}</td>
                        <td className="px-4 py-3 text-right text-slate-600">{spec.gsm}</td>
                        <td className="px-4 py-3 text-right font-semibold text-cyan-600">{spec.rim_weight_kg?.toFixed(3)}</td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handleDeleteSpec(spec.id)}
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'inward' && (
          <div className="p-6">
            <h2 className="text-lg font-semibold mb-4">Paper Inward History</h2>
            {inwardRecords.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <ArrowDownCircle size={48} className="mx-auto mb-4 text-slate-300" />
                <p>No inward records yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Paper Type</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600">Weight (kg)</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600">Rims</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Supplier</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Invoice</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {inwardRecords.map(record => (
                      <tr key={record.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-slate-600">{new Date(record.created_at).toLocaleDateString()}</td>
                        <td className="px-4 py-3 font-medium text-slate-900">{record.paper_spec_name}</td>
                        <td className="px-4 py-3 text-right text-emerald-600 font-semibold">{record.total_weight_kg?.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right text-blue-600 font-semibold">{record.total_rims?.toLocaleString()}</td>
                        <td className="px-4 py-3 text-slate-600">{record.supplier_name || '-'}</td>
                        <td className="px-4 py-3 text-slate-600">{record.invoice_number || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'consumption' && (
          <div className="p-6">
            <h2 className="text-lg font-semibold mb-4">Consumption History</h2>
            <p className="text-sm text-slate-500 mb-4">Formula: Rims Consumed = (Books × Pages) / 8000</p>
            {consumptionRecords.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <ArrowUpCircle size={48} className="mx-auto mb-4 text-slate-300" />
                <p>No consumption records yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Book (SKU)</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Paper Type</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600">Books</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600">Pages/Book</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600">Base Rims</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600">Wastage</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600">Total Used</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600">Rims Left</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {consumptionRecords.map(record => (
                      <tr key={record.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-slate-600">{new Date(record.created_at).toLocaleDateString()}</td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-slate-900">{record.product_sku || record.product_name || record.job_name}</p>
                          {record.product_name && record.product_sku && (
                            <p className="text-xs text-slate-500">{record.product_name}</p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-600">{record.paper_spec_name}</td>
                        <td className="px-4 py-3 text-right text-slate-600">{record.number_of_books?.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right text-slate-600">{record.pages_per_book}</td>
                        <td className="px-4 py-3 text-right text-slate-600">{record.base_rims || '-'}</td>
                        <td className="px-4 py-3 text-right text-red-500 text-xs">
                          {record.wastage_rims ? `+${record.wastage_rims} (${record.wastage_percent || 4}%)` : '-'}
                        </td>
                        <td className="px-4 py-3 text-right text-orange-600 font-semibold">{record.rims_consumed}</td>
                        <td className="px-4 py-3 text-right text-blue-600 font-semibold">{record.rims_after}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add Paper Spec Modal */}
      {showSpecModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Add Paper Specification</h3>
              <button onClick={() => setShowSpecModal(false)} className="p-1 hover:bg-slate-100 rounded">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreateSpec} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={specForm.name}
                  onChange={(e) => setSpecForm({...specForm, name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500"
                  placeholder="e.g., A4 70GSM"
                  required
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Width (cm) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={specForm.width}
                    onChange={(e) => setSpecForm({...specForm, width: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Length (cm) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={specForm.length}
                    onChange={(e) => setSpecForm({...specForm, length: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">GSM *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={specForm.gsm}
                    onChange={(e) => setSpecForm({...specForm, gsm: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500"
                    required
                  />
                </div>
              </div>
              {calculateRimWeight() && (
                <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3">
                  <p className="text-sm text-cyan-800">
                    <Calculator className="inline h-4 w-4 mr-1" />
                    Calculated Rim Weight: <strong>{calculateRimWeight()} kg</strong>
                  </p>
                  <p className="text-xs text-cyan-600 mt-1">Formula: Width × Length × GSM / 20,000</p>
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowSpecModal(false)}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitting ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />}
                  Add
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Paper Inward Modal */}
      {showInwardModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between p-4 border-b bg-emerald-50">
              <h3 className="text-lg font-semibold text-emerald-800 flex items-center gap-2">
                <ArrowDownCircle size={20} />
                Record Paper Inward
              </h3>
              <button onClick={() => setShowInwardModal(false)} className="p-1 hover:bg-emerald-100 rounded">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreateInward} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Paper Type *</label>
                <select
                  value={inwardForm.paper_spec_id}
                  onChange={(e) => setInwardForm({...inwardForm, paper_spec_id: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500"
                  required
                >
                  <option value="">Select Paper Type</option>
                  {paperSpecs.map(spec => (
                    <option key={spec.id} value={spec.id}>{spec.name} (Rim: {spec.rim_weight_kg?.toFixed(3)} kg)</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Total Weight (kg) *</label>
                <input
                  type="number"
                  step="0.01"
                  value={inwardForm.total_weight_kg}
                  onChange={(e) => setInwardForm({...inwardForm, total_weight_kg: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500"
                  required
                />
              </div>
              {calculateRimsFromWeight() && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                  <p className="text-sm text-emerald-800">
                    <Calculator className="inline h-4 w-4 mr-1" />
                    This equals: <strong>{calculateRimsFromWeight()} Rims</strong>
                  </p>
                  <p className="text-xs text-emerald-600 mt-1">Formula: Total Weight / Rim Weight</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Supplier Name</label>
                <input
                  type="text"
                  value={inwardForm.supplier_name}
                  onChange={(e) => setInwardForm({...inwardForm, supplier_name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Invoice Number</label>
                <input
                  type="text"
                  value={inwardForm.invoice_number}
                  onChange={(e) => setInwardForm({...inwardForm, invoice_number: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowInwardModal(false)}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitting ? <Loader2 className="animate-spin" size={16} /> : <ArrowDownCircle size={16} />}
                  Record Inward
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Paper Consumption Modal */}
      {showConsumptionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between p-4 border-b bg-orange-50">
              <h3 className="text-lg font-semibold text-orange-800 flex items-center gap-2">
                <ArrowUpCircle size={20} />
                Record Consumption
              </h3>
              <button onClick={() => setShowConsumptionModal(false)} className="p-1 hover:bg-orange-100 rounded">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreateConsumption} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Paper Type *</label>
                <select
                  value={consumptionForm.paper_spec_id}
                  onChange={(e) => setConsumptionForm({...consumptionForm, paper_spec_id: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  required
                >
                  <option value="">Select Paper Type</option>
                  {paperSpecs.map(spec => {
                    const stock = paperStock.find(s => s.paper_spec_id === spec.id);
                    return (
                      <option key={spec.id} value={spec.id}>
                        {spec.name} (Available: {stock?.current_rims?.toFixed(2) || 0} rims)
                      </option>
                    );
                  })}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Book (SKU) *</label>
                <select
                  value={consumptionForm.product_id}
                  onChange={(e) => handleProductChange(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  required
                >
                  <option value="">Select Book</option>
                  {products.map(product => (
                    <option key={product.id} value={product.id}>
                      {product.sku} - {product.name} {product.pages ? `(${product.pages} pages)` : ''}
                    </option>
                  ))}
                </select>
                {consumptionForm.product_id && !products.find(p => p.id === consumptionForm.product_id)?.pages && (
                  <p className="text-xs text-amber-600 mt-1">⚠️ Pages not set for this book. Please enter manually below.</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Number of Books *</label>
                  <input
                    type="number"
                    value={consumptionForm.number_of_books}
                    onChange={(e) => setConsumptionForm({...consumptionForm, number_of_books: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Pages per Book {consumptionForm.pages_per_book ? '' : '*'}</label>
                  <input
                    type="number"
                    value={consumptionForm.pages_per_book}
                    onChange={(e) => setConsumptionForm({...consumptionForm, pages_per_book: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 ${consumptionForm.pages_per_book ? 'bg-emerald-50 border-emerald-300' : ''}`}
                    placeholder="Auto-filled from product"
                  />
                  {consumptionForm.pages_per_book && (
                    <p className="text-xs text-emerald-600 mt-1">✓ Auto-filled from product</p>
                  )}
                </div>
              </div>
              {calculateConsumption() && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 space-y-1">
                  <p className="text-sm text-orange-800">
                    <Calculator className="inline h-4 w-4 mr-1" />
                    Total Pages: <strong>{calculateConsumption().totalPages.toLocaleString()}</strong>
                  </p>
                  <p className="text-sm text-orange-800">
                    Base Rims: <strong>{calculateConsumption().baseRims}</strong>
                  </p>
                  <p className="text-sm text-red-600">
                    + Wastage (4%): <strong>{calculateConsumption().wastageRims}</strong>
                  </p>
                  <p className="text-sm text-orange-900 font-semibold border-t border-orange-300 pt-1 mt-1">
                    Total Rims to deduct: <strong>{calculateConsumption().rimsConsumed}</strong>
                  </p>
                  <p className="text-sm text-orange-800">
                    Weight to be consumed: <strong>{calculateConsumption().weightConsumed} kg</strong>
                  </p>
                  <p className="text-xs text-orange-600 mt-1">Formula: ((Books × Pages) / 8000) × 1.04 = Rims</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Remarks</label>
                <input
                  type="text"
                  value={consumptionForm.remarks}
                  onChange={(e) => setConsumptionForm({...consumptionForm, remarks: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowConsumptionModal(false)}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitting ? <Loader2 className="animate-spin" size={16} /> : <ArrowUpCircle size={16} />}
                  Record Consumption
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaperStock;
